import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const CustomerAuthContext = createContext(null);

/**
 * Public-site customer authentication.
 * Completely separate from the internal staff AuthContext.
 * Uses the httpOnly `customer_token` cookie set by /api/public/customers/*.
 */
export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/public/customers/me");
      setCustomer(data);
      return data;
    } catch {
      setCustomer(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me check when landing from Google OAuth — PublicAuthCallback will
    // exchange the session_id and set the cookie first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);


  const signup = useCallback(async (payload) => {
    const { data } = await api.post("/public/customers/signup", payload);
    // If verification required, customer isn't logged in yet
    if (data.pending_verification) return {
      pendingVerification: true,
      email: data.email,
      emailDeliveryFailed: !!data.email_delivery_failed,
      debugOtp: data.debug_otp || null,
      message: data.message,
    };
    // Otherwise (rare — old flow) fall back to auto-login
    if (data.customer) setCustomer(data.customer);
    return { pendingVerification: false, customer: data.customer };
  }, []);

  const login = useCallback(async (payload) => {
    const { data } = await api.post("/public/customers/login", payload);
    setCustomer(data.customer);
    return data.customer;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/public/customers/logout"); } catch { /* ignore */ }
    setCustomer(null);
  }, []);

  const updateProfile = useCallback(async (payload) => {
    const { data } = await api.patch("/public/customers/me", payload);
    setCustomer(data);
    return data;
  }, []);

  return (
    <CustomerAuthContext.Provider value={{ customer, loading, signup, login, logout, updateProfile, refresh }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error("useCustomerAuth must be used within CustomerAuthProvider");
  return ctx;
}
